import {
  AppCard,
  Button,
  Icon,
  PageContainer,
  RouteButtonLink,
  RouteIconButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiClose, mdiEye, mdiInformation, mdiScriptTextPlay } from "@mdi/js";
import {
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { listCallOffContractItems } from "../../../__generated__/orval/endpoints/call-off-contract-items/call-off-contract-items";
import { createCreatePurchaseOrderByCallOffContract } from "../../../__generated__/orval/endpoints/create-purchase-order-by-call-off-contract/create-purchase-order-by-call-off-contract";
import { EntitiesCallOffContractItemCollectionListResponse } from "../../../__generated__/orval/models/entitiesCallOffContractItemCollectionListResponse.zod";
import type { EntitiesCallOffContractItemCollectionMainOutput } from "../../../__generated__/orval/models/entitiesCallOffContractItemCollectionMain.zod";
import { ForbiddenError } from "../../../errors/ForbiddenError";
import { useAuth } from "../../../hooks/useAuth";
import { getMicrosoftToken } from "../../../services/getMicrosoftToken";
import { i18nextParseToDate } from "../../../utils/i18nextParseTo";
import {
  CallOffContractFilters,
  type CallOffContractFiltersValue,
} from "./-internal_components/CallOffContractFilters";
import { CallOffContractPurchaseOrderPreviewModal } from "./-internal_components/CallOffContractPurchaseOrderPreviewModal";

const MAXIMUM_SELECTED_ITEMS = 100;
const sortFields = [
  "identifier",
  "contract",
  "request_number",
  "sap_created_at",
  "tracking_number",
  "requester",
] as const;
type SortField = (typeof sortFields)[number];

export const searchSchema = z.object({
  itemsPerPage: z.number().int().min(1).max(100).catch(25).default(25),
  orderBy: z.enum(sortFields).optional().catch(undefined),
  orderDirection: z.enum(["asc", "desc"]).optional().catch(undefined),
  page: z.number().int().min(1).catch(1).default(1),
  plantIds: z.array(z.number()).optional().catch(undefined),
  prNumber: z.number().optional().catch(undefined),
  purchaseContract: z.string().optional().catch(undefined),
  purchaseGroupIds: z.array(z.number()).optional().catch(undefined),
  referenceNumber: z.string().optional().catch(undefined),
  requesterIds: z.array(z.number()).optional().catch(undefined),
  supplierIds: z.array(z.number()).optional().catch(undefined),
  trackingNumber: z.string().optional().catch(undefined),
});

type CallOffItem = EntitiesCallOffContractItemCollectionMainOutput;

const CallOffContractIssuePurchaseOrder: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const servicePriceFormat = new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  const materialPriceFormat = new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: 3,
    minimumFractionDigits: 2,
  });
  const canGeneratePurchaseOrder = Boolean(
    user?.roles_names.includes("Call-Off Contract - Generate Purchase Order"),
  );
  const [items, setItems] = useState<CallOffItem[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creatingPurchaseOrder, setCreatingPurchaseOrder] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [error, setError] = useState(false);
  const [groupErrors, setGroupErrors] = useState<(string | null)[] | undefined>(
    undefined,
  );

  const filters = useMemo<CallOffContractFiltersValue>(
    () => ({
      plantIds: search.plantIds,
      prNumber: search.prNumber,
      purchaseContract: search.purchaseContract,
      purchaseGroupIds: search.purchaseGroupIds,
      referenceNumber: search.referenceNumber,
      requesterIds: search.requesterIds,
      supplierIds: search.supplierIds,
      trackingNumber: search.trackingNumber,
    }),
    [
      search.plantIds,
      search.prNumber,
      search.purchaseContract,
      search.purchaseGroupIds,
      search.referenceNumber,
      search.requesterIds,
      search.supplierIds,
      search.trackingNumber,
    ],
  );

  const fetchItems = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      setLoading(true);
      setError(false);

      try {
        const response = await listCallOffContractItems(
          {
            execution_status: "pending",
            items_per_page: search.itemsPerPage,
            order_by: search.orderBy,
            order_direction: search.orderBy
              ? (search.orderDirection ?? "asc")
              : undefined,
            page: search.page,
            plant_ids: search.plantIds,
            pr_number: search.prNumber,
            purchase_contract: search.purchaseContract,
            purchase_group_ids: search.purchaseGroupIds,
            reference_number: search.referenceNumber,
            requester_ids: search.requesterIds,
            supplier_ids: search.supplierIds,
            tracking_number: search.trackingNumber,
          },
          undefined,
          signal,
        );

        if (signal?.aborted) return;

        const parsed =
          EntitiesCallOffContractItemCollectionListResponse.safeParse(response);

        if (!parsed.success) {
          setItems([]);
          setTotalRows(0);
          setError(true);
          return;
        }

        setItems(parsed.data.results);
        setTotalRows(parsed.data.total_items);
      } catch {
        if (signal?.aborted) return;

        setItems([]);
        setTotalRows(0);
        setError(true);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [
      search.itemsPerPage,
      search.orderBy,
      search.orderDirection,
      search.page,
      search.plantIds,
      search.prNumber,
      search.purchaseContract,
      search.purchaseGroupIds,
      search.referenceNumber,
      search.requesterIds,
      search.supplierIds,
      search.trackingNumber,
    ],
  );

  useEffect(() => {
    const controller = new AbortController();

    void fetchItems(controller.signal);

    return () => controller.abort();
  }, [fetchItems]);

  const handleFiltersApply = useCallback(
    (nextFilters: CallOffContractFiltersValue): void => {
      setSelected([]);
      navigate({
        search: (previous) => ({
          ...previous,
          ...nextFilters,
          page: 1,
        }),
      });
    },
    [navigate],
  );

  const handleFiltersClear = useCallback((): void => {
    setSelected([]);
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        plantIds: undefined,
        prNumber: undefined,
        purchaseContract: undefined,
        purchaseGroupIds: undefined,
        referenceNumber: undefined,
        requesterIds: undefined,
        supplierIds: undefined,
        trackingNumber: undefined,
      }),
    });
  }, [navigate]);

  const handleChangePage = useCallback(
    (page: number): void => {
      setSelected([]);
      navigate({
        search: (previous) => ({
          ...previous,
          page,
        }),
      });
    },
    [navigate],
  );

  const handleChangeRowsPerPage = useCallback(
    (itemsPerPage: number): void => {
      setSelected([]);
      navigate({
        search: (previous) => ({
          ...previous,
          itemsPerPage,
          page: 1,
        }),
      });
    },
    [navigate],
  );

  const handleSort = useCallback(
    (column: SortField): void => {
      setSelected([]);
      navigate({
        search: (previous) => {
          const orderDirection =
            previous.orderBy === column
              ? previous.orderDirection === "asc"
                ? "desc"
                : previous.orderDirection === "desc"
                  ? undefined
                  : "asc"
              : "asc";

          return {
            ...previous,
            orderBy: orderDirection ? column : undefined,
            orderDirection,
            page: 1,
          };
        },
      });
    },
    [navigate],
  );

  const handleSelectAll = (checked: boolean): void => {
    if (checked) {
      setSelected((current) => [
        ...new Set([
          ...current,
          ...items
            .map((item) => item.id)
            .slice(0, MAXIMUM_SELECTED_ITEMS - current.length),
        ]),
      ]);
      return;
    }

    setSelected((current) =>
      current.filter((id) => !items.some((item) => item.id === id)),
    );
  };

  const handleSelect = (id: string): void => {
    setSelected((current) => {
      if (current.includes(id)) {
        return current.filter((selectedId) => selectedId !== id);
      }
      return current.length < MAXIMUM_SELECTED_ITEMS
        ? [...current, id]
        : current;
    });
  };

  const handleConfirmGenerate = async (groups: string[][]): Promise<void> => {
    setCreatingPurchaseOrder(true);
    setGroupErrors(undefined);

    try {
      const authenticated = await getMicrosoftToken("procurement");
      if (!authenticated) {
        return;
      }

      const createResults = await Promise.allSettled(
        groups.map((ids) =>
          createCreatePurchaseOrderByCallOffContract({
            call_off_item_ids: ids,
          }),
        ),
      );

      const errors = createResults.map((r) =>
        r.status === "rejected"
          ? ((r.reason as { response?: { data?: { error?: string } } })
              ?.response?.data?.error ?? "Unknown error")
          : null,
      );

      if (errors.some((e) => e != null)) {
        setGroupErrors(errors);
        return;
      }

      setSelected([]);
      setIsPreviewModalOpen(false);
      await fetchItems();
    } catch {
      setIsPreviewModalOpen(false);
      setError(true);
    } finally {
      setCreatingPurchaseOrder(false);
    }
  };

  const allVisibleSelected =
    items.length > 0 && items.every((item) => selected.includes(item.id));

  return (
    <PageContainer
      maxWidth="xl"
      showBackButton={false}
      title={t("callOffContract.title")}
    >
      <AppCard>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography color="text.secondary" variant="body2">
            {t("callOffContract.groupingMessage")}
          </Typography>
          <RouteButtonLink
            startIcon={<Icon icon={mdiScriptTextPlay} />}
            to="/create_purchase_order_by_call_off_contracts"
            variant="outlined"
          >
            {t("issuePurchaseOrder.trackExecutions")}
          </RouteButtonLink>
        </Box>

        <CallOffContractFilters
          disabled={loading}
          filters={filters}
          onApply={handleFiltersApply}
          onClear={handleFiltersClear}
        />

        {!!error && (
          <Box sx={{ color: "error.main", py: 2 }}>
            {t("callOffContract.loadError")}
          </Box>
        )}

        {selected.length > 0 ? (
          <Box
            sx={{
              alignItems: "center",
              bgcolor: "tokens.mutedSurface",
              display: "flex",
              gap: 2,
              my: 2,
              p: 1,
            }}
          >
            <Button
              aria-label={t("callOffContract.clearSelection")}
              onClick={() => setSelected([])}
              variant="text"
            >
              <Icon icon={mdiClose} />
            </Button>
            <Typography sx={{ flexGrow: 1 }}>
              {t("callOffContract.selectedItems", {
                selected: selected.length,
              })}
            </Typography>
            <Tooltip
              title={
                canGeneratePurchaseOrder
                  ? ""
                  : t("callOffContract.noPermissionToGenerate")
              }
            >
              <span>
                <Button
                  disabled={creatingPurchaseOrder || !canGeneratePurchaseOrder}
                  onClick={() => setIsPreviewModalOpen(true)}
                  startIcon={
                    creatingPurchaseOrder ? (
                      <CircularProgress size={16} />
                    ) : undefined
                  }
                  variant="contained"
                >
                  {t("callOffContract.generatePurchaseOrder")}
                </Button>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ alignItems: "center", display: "flex", gap: 1, my: 2 }}>
            <Tooltip
              title={t("callOffContract.selectionLimit", {
                count: MAXIMUM_SELECTED_ITEMS,
              })}
            >
              <Icon icon={mdiInformation} />
            </Tooltip>
            <Typography>
              {t("callOffContract.selectedItems", { selected: 0 })}
            </Typography>
          </Box>
        )}

        <CallOffContractPurchaseOrderPreviewModal
          callOffItemIds={selected}
          confirmLoading={creatingPurchaseOrder}
          groupErrors={groupErrors}
          onClose={() => setIsPreviewModalOpen(false)}
          onConfirm={handleConfirmGenerate}
          open={isPreviewModalOpen}
        />

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={selected.length > 0 && !allVisibleSelected}
                    onChange={(event) => handleSelectAll(event.target.checked)}
                  />
                </TableCell>
                <TableCell
                  sortDirection={
                    search.orderBy === "identifier"
                      ? search.orderDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={search.orderBy === "identifier"}
                    direction={
                      search.orderBy === "identifier"
                        ? (search.orderDirection ?? "asc")
                        : "asc"
                    }
                    onClick={() => handleSort("identifier")}
                  >
                    {t("callOffContract.referenceNumber")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    search.orderBy === "contract"
                      ? search.orderDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={search.orderBy === "contract"}
                    direction={
                      search.orderBy === "contract"
                        ? (search.orderDirection ?? "asc")
                        : "asc"
                    }
                    onClick={() => handleSort("contract")}
                  >
                    {t("callOffContract.contract")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    search.orderBy === "request_number"
                      ? search.orderDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={search.orderBy === "request_number"}
                    direction={
                      search.orderBy === "request_number"
                        ? (search.orderDirection ?? "asc")
                        : "asc"
                    }
                    onClick={() => handleSort("request_number")}
                  >
                    {t("callOffContract.prNumber")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    search.orderBy === "tracking_number"
                      ? search.orderDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={search.orderBy === "tracking_number"}
                    direction={
                      search.orderBy === "tracking_number"
                        ? (search.orderDirection ?? "asc")
                        : "asc"
                    }
                    onClick={() => handleSort("tracking_number")}
                  >
                    {t("callOffContract.trackingNumber")}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={
                    search.orderBy === "sap_created_at"
                      ? search.orderDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={search.orderBy === "sap_created_at"}
                    direction={
                      search.orderBy === "sap_created_at"
                        ? (search.orderDirection ?? "asc")
                        : "asc"
                    }
                    onClick={() => handleSort("sap_created_at")}
                  >
                    {t("common.createdAt")}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t("callOffContract.description")}</TableCell>
                <TableCell>{t("callOffContract.supplier")}</TableCell>
                <TableCell
                  sortDirection={
                    search.orderBy === "requester"
                      ? search.orderDirection
                      : false
                  }
                >
                  <TableSortLabel
                    active={search.orderBy === "requester"}
                    direction={
                      search.orderBy === "requester"
                        ? (search.orderDirection ?? "asc")
                        : "asc"
                    }
                    onClick={() => handleSort("requester")}
                  >
                    {t("callOffContract.requester")}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t("callOffContract.plant")}</TableCell>
                <TableCell>{t("common.quantity")}</TableCell>
                <TableCell>{t("callOffContract.itemType")}</TableCell>
                <TableCell align="right">{t("common.unitPrice")}</TableCell>
                <TableCell align="center" sx={{ width: 64 }}>
                  {t("common.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!!loading && (
                <TableRow>
                  <TableCell colSpan={14}>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14}>
                    {t("callOffContract.noItems")}
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                items.map((item) => (
                  <TableRow
                    hover
                    key={item.id}
                    onClick={() => handleSelect(item.id)}
                    selected={selected.includes(item.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(item.id)} />
                    </TableCell>
                    <TableCell>{item.reference_number}</TableCell>
                    <TableCell>
                      {item.purchase_contract} /{" "}
                      {item.purchase_contract_item || "-"}
                    </TableCell>
                    <TableCell>
                      {item.pr_number} / {item.item_number}
                    </TableCell>
                    <TableCell>{item.tracking_number || "-"}</TableCell>
                    <TableCell>
                      {item.sap_created_at
                        ? t("intlDate", i18nextParseToDate(item.sap_created_at))
                        : "-"}
                    </TableCell>
                    <TableCell>{item.description || "-"}</TableCell>
                    <TableCell>{item.supplier || "-"}</TableCell>
                    <TableCell>{item.requester || "-"}</TableCell>
                    <TableCell>{item.plant || "-"}</TableCell>
                    <TableCell>
                      {item.open_quantity == null
                        ? "-"
                        : [item.open_quantity, item.measure_unit]
                            .filter(Boolean)
                            .join(" ")}
                    </TableCell>
                    <TableCell>
                      {item.item_category === "9"
                        ? t("common.service")
                        : item.item_category === "0"
                          ? t("common.material")
                          : "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {item.total_price == null
                        ? "-"
                        : [
                            item.currency,
                            (item.item_category === "9"
                              ? servicePriceFormat
                              : materialPriceFormat
                            ).format(Number(item.total_price)),
                          ]
                            .filter(Boolean)
                            .join(" ")}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={t("callOffContract.serviceLines.viewTooltip")}
                      >
                        <RouteIconButtonLink
                          aria-label={t(
                            "callOffContract.serviceLines.viewLabel",
                            {
                              item: item.item_number,
                              pr: item.pr_number,
                            },
                          )}
                          disabled={item.item_category !== "9"}
                          onClick={(event) => event.stopPropagation()}
                          params={{ callOffItemId: item.id }}
                          search={search}
                          to="/call_off_contract_items/$callOffItemId/service_lines"
                        >
                          <Icon icon={mdiEye} />
                        </RouteIconButtonLink>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={14}
                  count={totalRows}
                  onPageChange={(_event, page) => handleChangePage(page + 1)}
                  onRowsPerPageChange={(event) =>
                    handleChangeRowsPerPage(Number(event.target.value))
                  }
                  page={search.page - 1}
                  rowsPerPage={search.itemsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  showFirstButton
                  showLastButton
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/call_off_contract_items/issue_purchase_order",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Call-Off Contract - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: CallOffContractIssuePurchaseOrder,
});
